import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import PhoneInput from "@/components/PhoneInput";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { getDefaultCountryByBrowserLanguage } from "@/constants/countries";
import { getUtmParams } from "@/lib/utm-params";
import { registerUser } from "@/lib/auth/registerUser";
import { getGaClientId } from "@/lib/analytics/gaClientId";
import { normalizeRegisterError } from "@/lib/auth/normalizeRegisterError";
import { useTranslation, Trans } from "react-i18next";

interface LoginRegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const LoginRegisterModal = ({ isOpen, onClose, onSuccess }: LoginRegisterModalProps) => {
  const [activeTab, setActiveTab] = useState("login");
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const { login } = useAuth();
  const { t, i18n } = useTranslation();

  // Login form state
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // Register form state
  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    phone: "",
    country: getDefaultCountryByBrowserLanguage(),
    password: "",
    confirmPassword: "",
  });
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailMarketingOptIn, setEmailMarketingOptIn] = useState(true);

  const handleLoginInputChange = (field: string, value: string) => {
    setLoginData(prev => ({
      ...prev,
      [field]: value
    }));
    if (serverError) setServerError("");
  };

  const handleRegisterInputChange = (field: string, value: string) => {
    setRegisterData(prev => ({
      ...prev,
      [field]: value
    }));
    if (serverError) setServerError("");
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setServerError("");

    try {
      await login(loginData.email, loginData.password);
      onSuccess();
    } catch {
      // Error handling is done in the auth context, but we can show a generic message
      setServerError(t('loginRegisterModal.invalidCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (registerData.password !== registerData.confirmPassword) {
      setServerError(t('loginRegisterModal.passwordMismatch'));
      return;
    }

    setIsLoading(true);
    setServerError("");

    try {
      // Register the user. ToS/Privacy acceptance is implied by submitting the
      // form (see the notice below the button) and stamped server-side. Job title
      // is collected post-login by the ProfileCompletionModal, not here.
      await registerUser({
        email: registerData.email,
        name: registerData.name,
        password: registerData.password,
        phone: registerData.phone,
        country: registerData.country,
        locale: i18n.language === 'en' ? 'en' : 'pt',
        emailMarketingOptIn,
        utmParams: getUtmParams(),
        gaClientId: getGaClientId(),
      });

      // Login the user after successful registration
      await login(registerData.email, registerData.password);
      onSuccess();
    } catch (error: unknown) {
      console.error('Registration failed:', error);

      const { code, message } = normalizeRegisterError(error);
      if (code === 'emailTaken') {
        setServerError(t('loginRegisterModal.emailTaken'));
      } else if (code === 'invalidEmail') {
        setServerError(t('loginRegisterModal.invalidEmail'));
      } else {
        setServerError(message || t('loginRegisterModal.registrationError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form states
    setLoginData({ email: "", password: "" });
    setRegisterData({
      name: "",
      email: "",
      phone: "",
      country: getDefaultCountryByBrowserLanguage(),
      password: "",
      confirmPassword: "",
    });
    setServerError("");
    setActiveTab("login");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-gray-800 border-gray-700">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <img
              src="/images/autoclipper-logo-full.png"
              alt="Autoclipper Logo"
              className="h-10 w-auto"
            />
          </div>
          <DialogTitle className="text-center text-white">
            {t('loginRegisterModal.title')}
          </DialogTitle>
        </DialogHeader>

        {serverError && (
          <div className="mb-4 p-3 bg-red-900/20 border border-red-500 rounded-md">
            <p className="text-red-400 text-sm">{serverError}</p>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-gray-700">
            <TabsTrigger value="login" className="text-white data-[state=active]:bg-gray-600">
              {t('loginRegisterModal.loginTab')}
            </TabsTrigger>
            <TabsTrigger value="register" className="text-white data-[state=active]:bg-gray-600">
              {t('loginRegisterModal.registerTab')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="space-y-4 mt-4">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email" className="text-white">
                  {t('loginRegisterModal.emailLabel')}
                </Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder={t('loginRegisterModal.emailPlaceholder')}
                  value={loginData.email}
                  onChange={(e) => handleLoginInputChange("email", e.target.value)}
                  required
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password" className="text-white">
                  {t('loginRegisterModal.passwordLabel')}
                </Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showLoginPassword ? "text" : "password"}
                    placeholder={t('loginRegisterModal.passwordPlaceholder')}
                    value={loginData.password}
                    onChange={(e) => handleLoginInputChange("password", e.target.value)}
                    required
                    className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                  >
                    {showLoginPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <a
                  href="/forgot-password"
                  className="text-sm text-green-400 hover:text-green-300 underline"
                  onClick={handleClose}
                >
                  {t('loginRegisterModal.forgotPassword')}
                </a>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-lime-400 to-cyan-400 hover:from-lime-500 hover:to-cyan-500 text-black font-semibold"
                disabled={isLoading}
              >
                {isLoading ? t('loginRegisterModal.loggingIn') : t('loginRegisterModal.loginButton')}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register" className="space-y-4 mt-4">
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="register-name" className="text-white">
                  {t('loginRegisterModal.nameLabel')}
                </Label>
                <Input
                  id="register-name"
                  type="text"
                  placeholder={t('loginRegisterModal.namePlaceholder')}
                  value={registerData.name}
                  onChange={(e) => handleRegisterInputChange("name", e.target.value)}
                  required
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="register-email" className="text-white">
                  {t('loginRegisterModal.emailLabel')}
                </Label>
                <Input
                  id="register-email"
                  type="email"
                  placeholder={t('loginRegisterModal.emailPlaceholder')}
                  value={registerData.email}
                  onChange={(e) => handleRegisterInputChange("email", e.target.value)}
                  required
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                />
              </div>

              <PhoneInput
                value={registerData.phone}
                onChange={(value) => handleRegisterInputChange("phone", value)}
                country={registerData.country}
                onCountryChange={(country) => handleRegisterInputChange("country", country)}
                label={t('loginRegisterModal.phoneLabel')}
                className="text-white"
                required
              />

              <div className="space-y-2">
                <Label htmlFor="register-password" className="text-white">
                  {t('loginRegisterModal.passwordLabel')}
                </Label>
                <div className="relative">
                  <Input
                    id="register-password"
                    type={showRegisterPassword ? "text" : "password"}
                    placeholder={t('loginRegisterModal.passwordPlaceholder')}
                    value={registerData.password}
                    onChange={(e) => handleRegisterInputChange("password", e.target.value)}
                    required
                    className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                  >
                    {showRegisterPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="register-confirm-password" className="text-white">
                  {t('loginRegisterModal.confirmPasswordLabel')}
                </Label>
                <div className="relative">
                  <Input
                    id="register-confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={t('loginRegisterModal.confirmPasswordPlaceholder')}
                    value={registerData.confirmPassword}
                    onChange={(e) => handleRegisterInputChange("confirmPassword", e.target.value)}
                    required
                    className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Checkbox
                  id="register-marketing-opt-in"
                  checked={emailMarketingOptIn}
                  onCheckedChange={(next) => setEmailMarketingOptIn(next === true)}
                  className="mt-0.5 border-gray-500 data-[state=checked]:bg-lime-400 data-[state=checked]:text-black"
                />
                <Label
                  htmlFor="register-marketing-opt-in"
                  className="text-sm leading-snug text-gray-300 cursor-pointer font-normal"
                >
                  {t('register.marketingOptIn')}
                </Label>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-lime-400 to-cyan-400 hover:from-lime-500 hover:to-cyan-500 text-black font-semibold"
                disabled={isLoading}
              >
                {isLoading ? t('loginRegisterModal.creatingAccount') : t('loginRegisterModal.createAccountButton')}
              </Button>

              <p className="text-center text-xs text-gray-400">
                <Trans
                  i18nKey="register.tosNotice"
                  components={{
                    tos: (
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-400 hover:text-green-300 underline"
                      />
                    ),
                    privacy: (
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-400 hover:text-green-300 underline"
                      />
                    ),
                  }}
                />
              </p>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-600" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-gray-800 px-2 text-gray-400">{t('common.or')}</span>
                </div>
              </div>

              <GoogleSignInButton
                redirect={window.location.pathname + window.location.search}
                label={t('register.signUpWithGoogle')}
              />
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default LoginRegisterModal;